import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import i18n from 'i18next';

import { AnimatePresence, motion } from 'framer-motion';

import { Button } from 'uikit/buttons';

import CardMedia from 'uikit/cards/components/cardMedia';
import { Label } from 'uikit/labels';
import Typo from './typo';

import {
  previewContentPropTypes,
  contentPropTypes,
  actionsPropTypes,
  statusPropTypes,
  coverPropTypes,
  coverMediaPropTypes,
  contentContainerPropTypes,
  contentTopContainerPropTypes,
} from '../helpers/getPreviewContentPropTypes';

import styles from '../styles/uikitContentPreviewContent.module.scss';

const ALIGNMENT = {
  topLeft: styles.isAlignmentTopLeft,
  bottomLeft: styles.isAlignmentBottomLeft,
  topCenter: styles.isAlignmentTopCenter,
  bottomCenter: styles.isAlignmentBottomCenter,
  topRight: styles.isAlignmentTopRight,
  bottomRight: styles.isAlignmentBottomRight,
};

const PreviewContent = ({
  actions = [],
  alignment = 'topLeft',
  children = null,
  className = '',
  coverContent = null,
  description = '',
  details = [],
  hasActions = false,
  hasCover = false,
  hasStatus = false,
  statusClassName = '',
  statusIcon = null,
  statusText = '',
  testId = 'preview-content',
  title = '',
  trigger = null,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  let timeoutId;

  const handleMouseEnter = () => {
    timeoutId = setTimeout(() => setShouldRender(true), 500);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeoutId);
    setShouldRender(false);
  };

  useEffect(() => {
    const element = trigger?.current;

    if (!element) return setShouldRender(true);

    const events = [
      ['mouseenter', handleMouseEnter],
      ['mouseleave', handleMouseLeave],
    ];

    events.forEach((event) => element.addEventListener(...event));

    return () => {
      clearTimeout(timeoutId);
      events.forEach((event) => element.removeEventListener(...event));
    };
  }, []);

  return (
    <AnimatePresence exitBeforeEnter>
      {shouldRender && (
        <motion.span
          key="key"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.25 } }}
          exit={{ opacity: 0, transition: { duration: 0.25 } }}
          className={classnames(styles.popupContainer, className, {
            [ALIGNMENT[alignment]]: alignment,
          })}
        >
          <div className={styles.popupContainerInner} data-testid={testId}>
            {children}
            {!children && (
              <>
                <ContentContainer>
                  {(hasCover || hasStatus) && (
                    <ContentTopContainer>
                      {hasCover && <Cover>{coverContent}</Cover>}
                      {hasStatus && (
                        <Status className={statusClassName} icon={statusIcon} text={statusText} />
                      )}
                    </ContentTopContainer>
                  )}
                  <Content title={title} description={description} details={details} />
                </ContentContainer>
                {hasActions && <Actions actions={actions} />}
              </>
            )}
          </div>
        </motion.span>
      )}
    </AnimatePresence>
  );
};

const Content = ({ className = '', description = '', details = [], title = '' }) => {
  const renderDetail = ({ label, value, id }) =>
    value && (
      <div key={id} className={styles.popupDetailItems}>
        <Typo className={styles.popupDetailLabel} variant="s02" weight="bold">
          {i18n.t('label-with-colon', { label, defaultValue: `${label}:` })}
        </Typo>
        <Typo variant="s02" className={styles.popupCode}>
          {value}
        </Typo>
      </div>
    );

  return (
    <div className={className}>
      <Typo variant="d02" className={styles.isTruncated}>
        {title}
      </Typo>
      <Typo variant="s02" className={styles.isTruncated}>
        {description}
      </Typo>
      <div className={styles.popupDetails}>
        {details.map(({ label, value, id }) => renderDetail({ label, value, id }))}
      </div>
    </div>
  );
};

const Actions = ({ actions, className = '' }) => (
  <div className={classnames(styles.popupActionBar, className)}>
    {actions.map(({ text, icon, onClick, id }) => (
      <Button
        key={id}
        className={styles.popupActionBarButton}
        text={text}
        variant="ghost"
        icon={icon}
        onClick={onClick}
      />
    ))}
  </div>
);

const Status = ({ className = '', icon = '', text = '' }) => (
  <div className={classnames(styles.popupStatus, className)}>
    <Label icon={icon} text={text} />
  </div>
);

const Cover = ({ children, className = '' }) => (
  <div className={classnames(styles.popupContentTopInner, className)}>{children}</div>
);

const CoverMedia = ({ duration = '', image = '', type = '' }) => (
  <CardMedia variant="list" duration={duration} image={image} type={type} />
);

const ContentContainer = ({ children, className = '' }) => (
  <div className={classnames(styles.popupContent, className)}>{children}</div>
);

const ContentTopContainer = ({ children, className = '' }) => (
  <div className={classnames(styles.popupContentTop, className)}>{children}</div>
);

PreviewContent.Content = Content;
PreviewContent.Actions = Actions;
PreviewContent.Status = Status;
PreviewContent.Cover = Cover;
PreviewContent.CoverMedia = CoverMedia;
PreviewContent.ContentContainer = ContentContainer;
PreviewContent.ContentTopContainer = ContentTopContainer;

PreviewContent.propTypes = { ...previewContentPropTypes };
Content.propTypes = { ...contentPropTypes };
Actions.propTypes = { ...actionsPropTypes };
Status.propTypes = { ...statusPropTypes };
Cover.propTypes = { ...coverPropTypes };
CoverMedia.propTypes = { ...coverMediaPropTypes };
ContentContainer.propTypes = { ...contentContainerPropTypes };
ContentTopContainer.propTypes = { ...contentTopContainerPropTypes };

export default PreviewContent;
